/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Honest positioning section — replaces fabricated social proof.
 * Shows the problem → alternative → solution pattern.
 */

"use client";

import { FadeIn } from "./primitives/fade-in";

const cards = [
  {
    label: "The mess",
    description:
      "You estimate on a spreadsheet, text the number, track the job in your head, and invoice from QuickBooks. Five tools, nothing talks to each other.",
  },
  {
    label: "The alternative",
    description:
      "Enterprise software that costs $500/mo, takes 3 months to set up, and needs a full-time admin to run.",
  },
  {
    label: "What should exist",
    description:
      "One simple platform where your estimate becomes a proposal becomes a project becomes an invoice. Data flows. Nothing falls through.",
    highlighted: true,
  },
];

export function SocialProofSection() {
  return (
    <section
      className="relative py-16 md:py-24 bg-primary grain-texture"
      aria-label="Why Contractor Co-Pilot exists"
    >
      {/* Blueprint grid */}
      <div
        className="blueprint-grid absolute inset-0 opacity-10"
        aria-hidden="true"
      />

      <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
        {/* Headline */}
        <FadeIn>
          <h2 className="text-[clamp(24px,3.5vw,36px)] leading-[1.1] font-bold font-display text-primary-foreground text-center mb-4">
            Contractors don&rsquo;t need more software.{" "}
            <span className="text-secondary">They need less chaos.</span>
          </h2>
        </FadeIn>

        <FadeIn delay={100}>
          <p className="text-base text-primary-foreground/60 text-center max-w-2xl mx-auto mb-12">
            The tools exist. The problem is they don&rsquo;t talk to each other.
          </p>
        </FadeIn>

        {/* Three cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto">
          {cards.map((card, i) => (
            <FadeIn
              key={card.label}
              delay={i * 100}
              className={`rounded-xl p-5 md:p-6 ${
                card.highlighted
                  ? "border-2 border-secondary/40 bg-secondary/10"
                  : "border border-primary-foreground/10 bg-primary-foreground/5"
              } backdrop-blur-sm`}
            >
              <span
                className={`inline-block text-xs font-semibold uppercase tracking-wider mb-3 ${
                  card.highlighted
                    ? "text-secondary"
                    : "text-primary-foreground/50"
                }`}
              >
                {card.label}
              </span>
              <p
                className={`text-sm leading-relaxed ${
                  card.highlighted
                    ? "text-primary-foreground"
                    : "text-primary-foreground/80"
                }`}
              >
                {card.description}
              </p>
            </FadeIn>
          ))}
        </div>

        {/* Founder credibility */}
        <FadeIn delay={400}>
          <div className="mt-10 text-center">
            <blockquote className="text-sm text-primary-foreground/60 italic max-w-lg mx-auto">
              &ldquo;Built by someone who grew up in the trades. Not by a tech
              company guessing at what contractors need.&rdquo;
            </blockquote>
            <p className="text-xs text-primary-foreground/40 mt-2">
              &mdash; Founder, Contractor Co-Pilot
            </p>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
